<script setup lang="ts">
import SectionTitle from '../SectionTitle.vue';
import PlannerDetailReviewSummary from './PlannerDetailReviewSummary.vue';
import { type FragmentType, graphql } from '@/gql';
import ButtonFlat from 'theme/components/atoms/ButtonFlat.vue';

const props = defineProps<{
  plannerId: string;
  plannerReview: FragmentType<typeof PlannerDetailReviewSectionFragment>;
}>();

const PlannerDetailReviewSectionFragment = graphql(`
  fragment PlannerDetailReviewSection on PlannerReview {
    count
    latestReviews {
      id
      ...PlannerDetailReviewSummary
    }
    reviews {
      id
      ...PlannerDetailReviewSummary
    }
  }
`);

const { data, execute } = useLazyAsyncQuery(
  graphql(`
    query PlannerDetailReviewSectionModal($id: ID!) {
      planner(id: $id) {
        review {
          reviews {
            id
            ...PlannerDetailReviewSummary
          }
        }
      }
    }
  `),
  { id: props.plannerId.toString() },
  { apollo: { fetchPolicy: 'no-cache' } }
);

const plannerReview = computed(() =>
  getFragment(PlannerDetailReviewSectionFragment, props.plannerReview)
);
const displayedReviews = computed(() =>
  plannerReview.value.latestReviews.slice(0, 10)
);
const { ControlledModal, openModal } = useModalNamed();

const allReviews = computed(() => data.value?.planner?.review.reviews ?? []);

const onClick = async () => {
  if (allReviews.value.length === 0) {
    await execute();
  }
  openModal();
};
</script>

<template>
  <section :class="$style.section">
    <SectionTitle title="クチコミ" />
    <section :class="$style.section__contents">
      <ul :class="$style.reviews">
        <li v-for="review in displayedReviews" :key="review.id">
          <PlannerDetailReviewSummary :review="review" />
        </li>
      </ul>
      <p v-if="plannerReview.count <= 10" :class="$style.count">
        全{{ plannerReview.count }}件
      </p>
      <ButtonFlat
        v-else
        :class="[$style.button, $style.section__button]"
        @click="onClick"
        >クチコミをもっと見る（全{{ plannerReview.count }}件）</ButtonFlat
      >
      <ControlledModal closeable>
        <ul :class="[$style.modal__content, $style.reviews]">
          <li v-for="review in allReviews" :key="review.id">
            <PlannerDetailReviewSummary :review="review" />
          </li>
        </ul>
      </ControlledModal>
    </section>
  </section>
</template>

<style lang="scss" module>
.section {
  &__button {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }

  &__contents {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.button {
  width: 100%;
}

.count {
  margin-top: 32px;
  font-size: 16px;
  text-align: center;
}

.reviews {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.modal {
  &__content {
    padding: 32px;
    max-width: 80vw;
  }
}
</style>
